import type { JourneyPage } from './types'

export const practiceGrowth: JourneyPage = {
  slug: 'practice-growth',
  intro: [
    { text: '', highlight: 'January 2026: 80 clients. 60-hour weeks' },
    { text: 'No time to grow. No plan to ', highlight: 'exit' },
    { text: 'But what if something ', highlight: 'changed' },
    { text: '15 hours a week came ', highlight: 'back' },
    { text: '', highlight: '2031: 175 clients. £2.3m exit' },
    { text: 'This could be ', highlight: 'your story' },
  ],
  colorClass: 'growth',
  seo: {
    title: 'WealthSpace Practice Growth | Scale Your Advice Firm',
    description:
      'Grow your financial advice practice from 80 to 175 clients. Track AUM, revenue growth, and build exit value with AI-powered efficiency tools.',
    keywords:
      'business growth, adviser analytics, revenue tracking, AUM growth, financial adviser exit, practice valuation, scale advice firm, adviser productivity',
  },
  hero: {
    eyebrow: 'The 5-Year Plan: 2026 → 2031',
    title: 'From lifestyle practice to',
    titleAccent: '7-figure exit',
    subtitle:
      "Imagine starting January 2026 with 80 clients, 60-hour weeks, and no exit plan. Here's what could happen over five years with WealthSpace - and why the maths works. Some advisers do this in 3 years. Others take 7. The maths works either way.",
    microcopy: '',
  },
  journeySection: {
    label: 'The Story',
    title: 'From 80 clients to',
    titleAccent: '7-figure exit',
    subtitle: "The 5-year journey from overwhelmed sole practitioner to systemised, saleable practice.",
  },
  timeline: [
    {
      time: 'January 2026',
      icon: 'clock',
      iconColor: 'amber',
      title: 'The starting point',
      meta: '80 clients, 60-hour weeks',
      body: "You're running a practice from a small office. You have 80 clients, £34M under advice, and a problem: you're drowning in paperwork. Every suitability report takes 4-5 hours. Every review meeting needs a full day of prep. Your evenings are spent catching up. Your weekends are spent worrying.",
      previewLabel: 'The reality',
      previewText:
        "I'm running a decent practice, but I can't grow. I can't take on more clients because I'm already at capacity. I can't take time off because the work just piles up.",
    },
    {
      time: 'Year 2 - 2027',
      icon: 'sparkle',
      iconColor: 'amber',
      title: 'The shift',
      meta: '15 hours a week reclaimed',
      body: 'You start using WealthSpace. The first month is adjustment. The second month, something clicks. A pension transfer report that used to take 4 hours now takes 45 minutes of review. A retirement planning letter that used to take an evening is done in 20 minutes.',
      previewLabel: 'What changes',
      previewText:
        'Suitability reports: from 4-5 hours to 45 minutes. Review preparation: from a full day to 30 minutes. Compliance checks: built into every document automatically.',
    },
    {
      time: 'Year 3 - 2028',
      icon: 'chart',
      iconColor: 'amber',
      title: 'The growth',
      meta: '118 clients, £56M AUM',
      body: "With 15 extra hours a week, you have choices. You choose to grow. You start accepting referrals you'd previously turned away. You attend more networking events. You raise your minimum investment threshold from £100k to £250k - not to exclude clients, but because you can now afford to serve fewer clients better.",
      previewLabel: 'The mindset shift',
      previewText:
        'Stop thinking like a sole practitioner. Start thinking like a business owner. Work less, build more.',
    },
    {
      time: 'Year 4 - 2029',
      icon: 'users',
      iconColor: 'amber',
      title: 'The system',
      meta: 'Building enterprise value',
      body: "By year four, you've built something different: a practice that could run without you in the room. You hire your first junior adviser - not to do the paperwork (WealthSpace handles that), but to handle client relationships and meetings.",
      previewLabel: 'Enterprise value',
      previewText:
        "Documented processes that don't depend on your memory. Client relationships managed through the system. Compliance audit trail that any acquirer could review.",
    },
    {
      time: 'Year 5 - 2031',
      icon: 'shield',
      iconColor: 'amber',
      title: 'The options',
      meta: 'Exit-ready practice',
      body: "Maybe you're not planning to sell. But when a consolidator approaches you, you're curious. What you've built isn't just a client list - it's a systemised practice with documented processes, clean compliance records, and genuine recurring revenue.",
      previewLabel: 'What acquirers see',
      previewText:
        "Every client file is complete. Every process is documented. Every compliance check is traceable. There's no 'key man' risk because the system doesn't depend on your personal knowledge.",
    },
  ],
  result: {
    title: 'Potential exit:',
    titleAccent: '£2.3 million',
    body: "This is an illustrative projection, not a result. At 3.5x recurring revenue - a multiple sometimes seen for systemised practices with clean compliance and documented processes - a 2031 exit might be modelled like this: at 0.75% ongoing charge on £88M, that's £660k revenue × 3.5.",
    stats: [
      { value: '175', label: 'Projected clients in 2031' },
      { value: '£88M', label: 'Projected AUM' },
      { value: '£2.3M', label: 'Potential exit value' },
    ],
  },
  features: {
    label: 'How it works',
    title: 'Build value while',
    titleAccent: 'working less',
    subtitle:
      'The time you get back doesnt just improve your life. It increases the value of everything youre building.',
    items: [
      {
        icon: 'document',
        title: 'Systemised processes',
        body: 'Every workflow documented. Every process repeatable. No more key-man risk that scares away buyers.',
      },
      {
        icon: 'shield',
        title: 'Clean compliance',
        body: 'Full audit trail on every client interaction. Acquirers can review years of documented advice in hours.',
      },
      {
        icon: 'chart',
        title: 'Recurring revenue',
        body: "Predictable, documented income streams that acquirers pay premium multiples for. Not just clients - a business.",
      },
    ],
  },
  testimonial: {
    quote:
      'We built WealthSpace because we saw advisers trapped - working harder but not building value. The time you get back doesnt just improve your life. It increases the value of everything youre building.',
    authorName: 'The WealthSpace Team',
    authorRole: 'Built by Advisers, for Advisers',
    initials: 'WS',
  },
  cta: {
    title: 'Start building your',
    titleAccent: 'exit value today',
    subtitle:
      "Whether you're planning to sell or just want your evenings back, WealthSpace gives you the time to build something worth having.",
    note: 'Start reclaiming 15+ hours a week from day one.',
  },
}
